import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { checkNFCAccess, fetchUserPicture } from '../../../api/mobileapi';
import { AccessPointData } from '../../../api/types';
import { AdamsError } from '../../../utils/errors';
import { ErrorMessage } from '../../ErrorMessage/ErrorMessage';
import { HideInProduction } from '../../HideInProduction/HideInProduction';
import { useHomeData } from '../../LoadHomeData/HomeDataProvider';
import { NFCMockForm } from '../../NFCMockForm/NFCMockForm';
import { NFCScan } from '../../NFCScan/NFCScan';
import { NFCScanIndicator } from '../../NFCScanIndicator/NFCScanIndicator';
import { EmulateAccessPointResult } from '../EmulateAccessPointResult';

interface EmulateAccessPointStep2Props {
    accessInformation: AccessPointData;
    nfcData: AccessPointData | null;
    onResetStep2: () => void;
    setNfcData: React.Dispatch<React.SetStateAction<AccessPointData | null>>;
    translations: Record<string, string>;
}

export const EmulateAccessPointStep2: React.FC<EmulateAccessPointStep2Props> = ({
    accessInformation,
    translations,
    nfcData,
    onResetStep2,
    setNfcData,
}) => {
    const { currentLanguage } = useHomeData();
    const [rfid, setRfid] = useState<string | null>(null);
    const [scanErrorMessage, setScanErrorMessage] = useState<string | null>(null);
    const location = useLocation();

    useEffect(() => {
        setNfcData(null);
        setScanErrorMessage(null);
    }, [location]);

    const {
        data,
        error: nfcError,
        isLoading: nfcLoading,
        fetchStatus,
    } = useQuery<AccessPointData | undefined, AdamsError>(
        ['nfcEmulate', rfid?.toString().concat(accessInformation.accessPointCode)],
        () =>
            checkNFCAccess({
                rfid: rfid!,
                accessPointCode: accessInformation.accessPointCode,
                language: currentLanguage,
            }),
        {
            enabled: !!rfid && !!accessInformation,
        }
    );

    const {
        data: scannedUserPicture,
        error: pictureError,
        isLoading: pictureLoading,
    } = useQuery<string | undefined, AdamsError>(['userPicture', nfcData], () => fetchUserPicture(nfcData!), {
        enabled: !!nfcData,
    });

    useEffect(() => {
        if (data) {
            setNfcData(data);
        }
    }, [data]); //so ugly, TODO

    const loading = (nfcLoading || pictureLoading) && fetchStatus !== 'idle';
    const error = nfcError || pictureError;
    const getErrorMessage = () => {
        const err = nfcError?.response?.data?.error || pictureError?.response?.data?.error;
        return err || nfcError?.message || pictureError?.message;
    };

    const handleErrorMessage = useCallback((err: string | null) => {
        setScanErrorMessage(err);
    }, []);

    const handleDetect = useCallback(
        (result: any) => {
            if (result) {
                setScanErrorMessage(null);
                setRfid(result);
            } else {
                handleErrorMessage('invalid scan');
            }
        },
        [handleErrorMessage]
    );

    if (nfcData && !pictureLoading) {
        return (
            <>
                <EmulateAccessPointResult
                    data={nfcData}
                    onResetStep2={onResetStep2}
                    scannedUserPicture={scannedUserPicture!}
                    setRfid={setRfid}
                    translations={translations}
                />
                <NFCScan onScan={handleDetect} onError={handleErrorMessage} />
            </>
        );
    }

    return (
        <>
            <HideInProduction>
                <NFCMockForm onSubmit={handleDetect} />
            </HideInProduction>

            <NFCScan onScan={handleDetect} onError={handleErrorMessage} />

            {loading && <LinearProgress />}

            {scanErrorMessage && (
                <ErrorMessage
                    errorMessage={scanErrorMessage}
                    showReturnButton={false}
                    title={translations.GLOBAL_ERROR_TITLE}
                    translations={translations}
                />
            )}
            {error && (
                <ErrorMessage
                    errorMessage={getErrorMessage()}
                    showReturnButton={false}
                    title={translations.GLOBAL_ERROR_TITLE}
                    translations={translations}
                />
            )}

            {accessInformation && (
                <Box textAlign="center" mt={4}>
                    <Typography variant="h4">{accessInformation.accessPointCode}</Typography>
                    <Typography variant="subtitle2">{accessInformation.accessPointDescription}</Typography>
                </Box>
            )}

            <NFCScanIndicator translations={translations} />
        </>
    );
};
